import {
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ClientPassportButton } from "../../../../../components/buttons/ClientPassportButton";
import { YesNoButton } from "../../../../../components/buttons/YesNoButton";
import { CustomerActionButton } from "./CustomerActionButton";
import { StatusIcon } from "./StatusIcon";

export function CustomersDetailsTable(props) {
    const { customers, flags, setCustomerFlags } = props;
    const { t } = useTranslation();

    return (
        <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>{t("clients.table.firstName")}</TableCell>
                        <TableCell>{t("clients.table.middleName")}</TableCell>
                        <TableCell>{t("clients.table.lastName")}</TableCell>
                        <TableCell>{t("clients.table.companyName")}</TableCell>
                        <TableCell>{t("clients.table.email")}</TableCell>
                        <TableCell>{t("clients.table.phoneNumber")}</TableCell>
                        <TableCell align="center">{t("clients.table.isForeigner")}</TableCell>
                        <TableCell align="center">{t("clients.table.isJdg")}</TableCell>
                        <TableCell align="center">{t("clients.table.isNip")}</TableCell>
                        <TableCell align="center">{t("clients.table.hasDocs")}</TableCell>
                        <TableCell align="center">{t("clients.table.hasDetailed")}</TableCell>
                        <TableCell align="center">{t("clients.table.hasBasic")}</TableCell>
                        <TableCell align="center">{t("common.actions")}</TableCell>
                        <TableCell align="center">{t("clients.table.passport")}</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {(customers || []).map((c) => (
                        <CustomerRow
                            key={c.id}
                            customer={c}
                            flags={flags[c.id] ?? flagsPlaceholder}
                            setCustomerFlags={setCustomerFlags}
                        />
                    ))}
                    {(!customers || customers.length === 0) && (
                        <TableRow>
                            <TableCell colSpan={9}>
                                <Typography color="text.secondary">
                                    {t("agreements.customersData.noSelectedClients")}
                                </Typography>
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

const CustomerRow = (props) => {
    const { customer, flags, setCustomerFlags } = props;
    const isCompany = useMemo(() => customer.company != null, [customer]);

    const setIsForeigner = useCallback(
        (value) => {
            setCustomerFlags(customer.id, { ...flags, isForeigner: value });
        },
        [customer, flags, setCustomerFlags],
    );

    const setIsJdg = useCallback(
        (value) => {
            setCustomerFlags(customer.id, { ...flags, isJdg: value });
        },
        [customer, flags, setCustomerFlags],
    );

    const setIsNip = useCallback(
        (value) => {
            setCustomerFlags(customer.id, { ...flags, isNip: value });
        },
        [customer, flags, setCustomerFlags],
    );

    return (
        <TableRow key={customer.id}>
            <TableCell>{customer.person?.name || "-"}</TableCell>
            <TableCell>{customer.person?.name2 || "-"}</TableCell>
            <TableCell>{customer.person?.surname || "-"}</TableCell>
            <TableCell>{customer.company?.name || "-"}</TableCell>
            <TableCell>
                {customer.person?.email?.email_address ||
                    customer.company?.email?.email_address ||
                    "-"}
            </TableCell>
            <TableCell>
                {customer.person?.phone?.phone_number ||
                    customer.company?.phone?.phone_number ||
                    "-"}
            </TableCell>
            <TableCell align="center">
                {<YesNoButton value={flags.isForeigner} setValue={setIsForeigner} />}
            </TableCell>
            <TableCell align="center">
                {isCompany ? null : <YesNoButton value={flags.isJdg} setValue={setIsJdg} />}
            </TableCell>
            <TableCell align="center">
                {isCompany ? null : <YesNoButton value={flags.isNip} setValue={setIsNip} />}
            </TableCell>
            <TableCell align="center">
                {isCompany ? null : (
                    <StatusIcon ok={Boolean(customer.person?.has_id_documentation)} />
                )}
            </TableCell>
            <TableCell align="center">
                {isCompany ? null : <StatusIcon ok={Boolean(customer.person?.has_detailed_info)} />}
            </TableCell>
            <TableCell align="center">
                <StatusIcon
                    ok={Boolean(
                        isCompany
                            ? customer.company?.has_basic_info
                            : customer.person?.has_basic_info,
                    )}
                />
            </TableCell>
            <TableCell align="center">
                <CustomerActionButton customer={customer} size="small" />
            </TableCell>
            <TableCell align="center">
                <ClientPassportButton
                    clientId={customer.id}
                    clientType={customer.company ? "company" : "customer"}
                />
            </TableCell>
        </TableRow>
    );
};
const flagsPlaceholder = {
    isNip: false,
    isJdg: false,
    isForeigner: false,
};
